// Fleet (Workstream C, v2 design) — install-base inventory: per-install health/telemetry
// cards (now rendering REAL phone-home telemetry: vuln_summary, db_size_mb, user_count,
// component_versions, health_status, last_backup), version-adoption bars (vs the channel
// rollout target), aggregate fleet telemetry, and the orgs impacted by each install nested
// directly under that install. REAL data only (no mock).
//
// v2 design ref: design_handoff_admin_ui_upgrade/screens/02-fleet-v2.html (Screen 2).
// Tokens are authoritative in src/index.css. Semantic green/orange tints below are the
// literals the design ref uses directly (no exact token exists for those families).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import {
  Server, RefreshCw, Terminal, Building2, Key, AlertTriangle,
  ShieldAlert, Users, Check, Database, HardDriveDownload,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/shared/EmptyState';
import { CompanyChip } from '@/components/shared/CompanyChip';
import { MaskedKey } from '@/components/shared/MaskedKey';
import { personaBase } from '@/lib/persona';
import { fetchModulesCatalog, type ModuleCatalogRow } from '@/lib/api/modules';
import { TrialCountdown } from '@/components/trial/TrialCountdown';

// Short, dense label for a module toggle on a fleet row. Acronym-style codes
// (no underscore, ≤5 chars) read best uppercased (ciic → CIIC); everything else
// uses the catalog name (trust_pro → "Trust Pro").
function moduleLabel(m: ModuleCatalogRow): string {
  if (!m.code.includes('_') && m.code.length <= 5) return m.code.toUpperCase();
  return m.name;
}

// ── API contracts ──────────────────────────────────────────────────────
interface LastBackup {
  size_mb?: number | null;
  bytes?: number | null;
  backup_file?: string | null;
  at?: string | null;
}
interface FleetOverview {
  totals: {
    instances: number; healthy: number; stale: number;
    platform_licenses: number; active_orgs: number; backed_up?: number;
  };
  last_backup_at?: string | null;
  version_distribution: Array<{ version: string; count: number }>;
  org_billing_breakdown: Record<string, number>;
  health_breakdown?: Record<string, number>;
  vuln_totals?: Record<string, number>;
}
interface Instance {
  instance_id: string; hostname: string | null; product_version: string | null;
  last_seen_at: string | null; active: boolean; license_id: string;
  health_status?: string | null;
  db_size_mb?: number | null;
  user_count?: number | null;
  component_versions?: Record<string, string> | null;
  vuln_summary?: Record<string, number> | null;
  migration_status?: Record<string, unknown> | null;
  last_backup?: LastBackup | null;
  last_restore?: Record<string, unknown> | null;
  last_dr_test?: Record<string, unknown> | null;
  last_compliance?: Record<string, unknown> | null;
  last_incident?: Record<string, unknown> | null;
  license_ids?: string[] | null;
  // Universal company identity (server-resolved): the owning company + license key.
  organization_id?: string | null;
  organization_name?: string | null;
  scope?: string | null;
  license_key?: string | null; // VW-… (not the uuid)
  organizations?: Array<{ id: string; name: string }> | null;
}
interface OrgAlloc {
  is_trial?: boolean;
  trial_expires_at?: string | null;
  id?: string | null;
  license_id?: string | null;
  external_org_id: string; org_name: string | null; billing_state: string;
  current_vendor_count: number; allocated_vendor_limit: number; active: boolean; last_seen_at: string | null;
  modules?: Record<string, boolean> | null;
  // Phase 5 Group 2 / D-5-6: 'supplier' orgs (vendor orgs an on-prem install
  // creates per D-62) sit outside the org pool and wall. Read-only here —
  // the install reports it on phone-home; the console never sets it.
  org_kind?: 'tenant' | 'supplier' | null;
  // The tenant's own (promoted) account + the platform owner it runs under.
  organization_id?: string | null;
  account_organization_id?: string | null;
  account_name?: string | null;
}
interface ChannelTarget { channel: string; target_version: string; rollout: number; updated_at: string; }

// ── Health pill skins (design-ref literals) ──────────────────────────────
// healthy → green; degraded/stale → orange; unhealthy → red; unknown → muted.
const HEALTH_STYLE: Record<string, { bg: string; fg: string; dot: string; label: string }> = {
  healthy: { bg: 'var(--ps-pill-green-bg)', fg: 'var(--ps-pill-green-tx)', dot: 'var(--ps-pill-green-tx)', label: 'Healthy' },
  degraded: { bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)', dot: 'var(--ps-pill-amber-tx)', label: 'Degraded' },
  stale: { bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)', dot: 'var(--ps-pill-amber-tx)', label: 'Stale' },
  unhealthy: { bg: 'var(--ps-pill-red-bg)', fg: 'var(--status-expired)', dot: 'var(--status-expired)', label: 'Unhealthy' },
  unknown: { bg: 'var(--bg-muted)', fg: 'var(--text-secondary)', dot: 'var(--text-muted)', label: 'Unknown' },
};
function healthSkin(state: string | null | undefined) {
  const s = (state || 'unknown').toLowerCase();
  return HEALTH_STYLE[s] || HEALTH_STYLE.unknown;
}

// ── Shared style atoms (tokens-first; layout numerics mirror the v2 ref) ──
const sectionCard: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
  boxShadow: 'var(--shadow-card)', marginBottom: 16,
};
const cardHeaderRow: React.CSSProperties = {
  padding: '14px 20px', borderBottom: '1px solid var(--border)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
const cardTitle: React.CSSProperties = { fontSize: 14, fontWeight: 600 };
const panelTitle: React.CSSProperties = { fontSize: 13.5, fontWeight: 700 };
const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
};

// ── Per-install card ─────────────────────────────────────────────────────
function Stat({ label, value, muted }: { label: string; value: React.ReactNode; muted?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: muted ? 'var(--text-secondary)' : 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

// Vulns rendered from the REAL anonymous vuln_summary as "{c}C {h}H {m}M {l}L".
// Critical/High get red/amber weight when > 0. "—" only when no summary reported.
function VulnValue({ vs }: { vs: Record<string, number> | null | undefined }) {
  if (!vs) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const c = vs.critical ?? 0, h = vs.high ?? 0, m = vs.medium ?? 0, l = vs.low ?? 0;
  return (
    <span>
      <span style={{ color: c > 0 ? 'var(--status-expired)' : 'var(--text-secondary)', fontWeight: c > 0 ? 700 : 400 }}>{c}C </span>
      <span style={{ color: h > 0 ? 'var(--ps-pill-amber-tx)' : 'var(--text-secondary)', fontWeight: h > 0 ? 700 : 400 }}>{h}H </span>
      <span style={{ color: 'var(--text-secondary)' }}>{m}M {l}L</span>
    </span>
  );
}

const STALE_BACKUP_MS = 7 * 24 * 3600 * 1000; // backups older than this are flagged amber

// "Last backup" line: size + relative time. Absent → muted "No backup yet". >7d → amber stale.
function LastBackupLine({ lb }: { lb: LastBackup | null | undefined }) {
  const at = lb?.at ?? null;
  if (!at) {
    return <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-muted)' }}><HardDriveDownload size={12} />No backup yet</span>;
  }
  const stale = Date.now() - new Date(at).getTime() > STALE_BACKUP_MS;
  const size = lb?.size_mb != null ? `${lb.size_mb.toLocaleString()} MB · ` : '';
  const color = stale ? 'var(--ps-pill-amber-tx)' : 'var(--text-secondary)';
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color }}>
      <HardDriveDownload size={12} />
      Last backup: {size}{formatRelativeTime(at)}{stale ? ' (stale)' : ''}
    </span>
  );
}

function pendingMigrations(ms: Record<string, unknown> | null | undefined): number {
  if (!ms) return 0;
  const p = (ms.pending as number) ?? 0;
  return typeof p === 'number' ? p : 0;
}

function licensePreview(inst: Instance): string {
  const id = (inst.license_ids && inst.license_ids[0]) || inst.license_id || '';
  return id.length > 20 ? `${id.slice(0, 20)}…` : id || '—';
}

// Match orgs to an install: an org belongs to an install when org.license_id ∈
// install.license_ids (fallback === install.license_id).
function instanceLicenseSet(inst: Instance): Set<string> {
  const ids = new Set<string>();
  (inst.license_ids || []).forEach((id) => id && ids.add(id));
  if (inst.license_id) ids.add(inst.license_id);
  return ids;
}

// Per-org utilisation row (nested under its install) — mirrors the design-ref
// allocation row but scoped to the install's impacted tenants.
function OrgRow({ o, moduleCatalog, onToggleModule }: { o: OrgAlloc; moduleCatalog: ModuleCatalogRow[]; onToggleModule?: (o: OrgAlloc, code: string) => void }) {
  const limit = o.allocated_vendor_limit || 0;
  const pct = limit > 0 ? Math.round((o.current_vendor_count / limit) * 100) : 0;
  const barColor = pct > 80 ? 'var(--status-expired)' : pct >= 50 ? 'var(--churn-medium)' : 'var(--accent)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
      <span style={{ flex: '0 0 160px', overflow: 'hidden' }}>
        <CompanyChip id={o.organization_id} name={o.org_name || o.external_org_id} origin={{ type: 'fleet' }} icon={false} size={12.5} weight={500} />
      </span>
      <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: 'var(--ps-pill-green-bg)', color: 'var(--ps-pill-green-tx)' }}>{o.billing_state}</span>
      <TrialCountdown isTrial={o.is_trial} expiresAt={o.trial_expires_at} />
      {o.org_kind === 'supplier' && (
        <span
          data-testid="org-kind-supplier"
          title="Supplier org (D-5-6): a vendor org the install created for a supplier — outside the org pool and the org wall, never counted against seats."
          style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}
        >
          supplier
        </span>
      )}
      <span style={{ fontSize: 12, fontFamily: 'var(--mono, monospace)', color: 'var(--text-secondary)', flex: '0 0 110px' }}>
        {o.current_vendor_count.toLocaleString()} / {limit ? limit.toLocaleString() : '∞'}
      </span>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 90 }}>
        {limit > 0 ? (
          <>
            <div style={{ flex: 1, height: 5, background: 'var(--bg-muted)', borderRadius: 99 }}>
              <div style={{ height: '100%', width: `${Math.min(Math.max(pct, 0.5), 100)}%`, background: barColor, borderRadius: 99 }} />
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, width: 30, textAlign: 'right' }}>{pct}%</span>
          </>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
        )}
      </div>
      {/* Per-module grant toggles (catalog-driven). Each flips this org's whole
          modules object on its next platform phone-home. */}
      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {moduleCatalog.length === 0 ? (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
        ) : (
          moduleCatalog.map((m) => {
            const enabled = o.modules?.[m.code] === true;
            const id = `mod-${m.code}-${o.external_org_id}`;
            return (
              <div
                key={m.code}
                style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                title={`Grants the ${m.name} module to this org on its next phone-home.`}
              >
                <input
                  type="checkbox"
                  id={id}
                  checked={enabled}
                  onChange={() => onToggleModule && onToggleModule(o, m.code)}
                  disabled={!o.id}
                  style={{ accentColor: 'var(--accent)', width: 13, height: 13, cursor: o.id ? 'pointer' : 'not-allowed' }}
                />
                <label
                  htmlFor={id}
                  style={{ fontSize: 11, fontWeight: 600, color: enabled ? 'var(--accent)' : 'var(--text-muted)', cursor: o.id ? 'pointer' : 'default', userSelect: 'none' }}
                >
                  {moduleLabel(m)}
                </label>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function InstanceCard({ inst, orgs, onDispatch, moduleCatalog, onToggleModule, moduleErrors }: { inst: Instance; orgs: OrgAlloc[]; onDispatch: (id: string) => void; moduleCatalog: ModuleCatalogRow[]; onToggleModule: (o: OrgAlloc, code: string) => void; moduleErrors?: Record<string, string> }) {
  const navigate = useNavigate();
  const location = useLocation();
  const base = personaBase(location.pathname);
  const fleetOrigin = { type: 'fleet', name: inst.instance_id };
  const hs = healthSkin(inst.health_status);
  const compCount = inst.component_versions ? Object.keys(inst.component_versions).length : null;
  const compList = inst.component_versions
    ? Object.entries(inst.component_versions).map(([k, v]) => `${k} ${v}`).join(' · ')
    : null;
  const pending = pendingMigrations(inst.migration_status);
  const orgCount = orgs.length;

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-card)', padding: '18px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: hs.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Server size={16} color={hs.fg} />
          </div>
          <div>
            {/* Lead with the universal company identity; the install id is secondary. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CompanyChip id={inst.organization_id} name={inst.organization_name} origin={fleetOrigin} size={14} fallback={inst.instance_id} />
              {inst.scope && (
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', padding: '1px 7px', borderRadius: 99, background: inst.scope === 'platform' ? 'var(--accent-light)' : 'var(--bg-muted)', color: inst.scope === 'platform' ? 'var(--accent)' : 'var(--text-secondary)' }}>{inst.scope}</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <span style={{ fontFamily: 'var(--mono, monospace)', fontSize: 11.5, color: 'var(--text-muted)' }}>{inst.instance_id}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: hs.bg, color: hs.fg }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: hs.dot }} />{hs.label}
              </span>
              <span style={{ fontFamily: 'var(--mono, monospace)', fontSize: 11.5, color: 'var(--text-secondary)' }}>v{inst.product_version || '—'}</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => onDispatch(inst.instance_id)}
          aria-label={`Dispatch op to ${inst.instance_id}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px',
            borderRadius: 7, background: 'var(--accent-light)', color: 'var(--accent)',
            border: '1px solid var(--accent-muted)', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <Terminal size={12} />Dispatch Op
        </button>
      </div>

      {/* Stats grid (REAL telemetry) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        <Stat label="Components" value={compCount != null ? compCount : <span style={{ color: 'var(--text-muted)' }}>—</span>} />
        <Stat label="Users" value={inst.user_count != null ? inst.user_count : <span style={{ color: 'var(--text-muted)' }}>—</span>} />
        <Stat label="DB (MB)" value={inst.db_size_mb != null ? inst.db_size_mb.toLocaleString() : <span style={{ color: 'var(--text-muted)' }}>—</span>} />
        <Stat label="Vulns" value={<VulnValue vs={inst.vuln_summary} />} />
        <Stat label="Last seen" value={inst.last_seen_at ? formatRelativeTime(inst.last_seen_at) : '—'} muted />
      </div>

      {/* Components detail line (real component_versions) */}
      {compList && (
        <div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'var(--mono, monospace)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {compList}
        </div>
      )}

      {/* Footer: orgs · license · last backup · pending migrations */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-secondary)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Building2 size={12} />{orgCount} org{orgCount !== 1 ? 's' : ''}</span>
        {/* License KEY (not the uuid) — links to the issued license, keeping context. */}
        {(inst.license_key || inst.license_id) && (
          <button
            onClick={() => navigate(`${base}/licenses/${inst.license_id || (inst.license_ids && inst.license_ids[0])}`, { state: { from: fleetOrigin } })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontFamily: 'inherit', fontSize: 12 }}
            title="Open the issued license"
          >
            <Key size={12} />
            {inst.license_key ? (
              <MaskedKey value={inst.license_key} size={11.5} />
            ) : (
              <span style={{ fontFamily: 'var(--mono, monospace)', fontSize: 11.5, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{licensePreview(inst)}</span>
            )}
          </button>
        )}
        <LastBackupLine lb={inst.last_backup} />
        {pending > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--ps-pill-amber-tx)', fontWeight: 500 }}>
            <AlertTriangle size={12} />{pending} pending migration{pending !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
        {([
          ['Last restore', inst.last_restore],
          ['Last DR test', inst.last_dr_test],
          ['Last compliance', inst.last_compliance],
          ['Last incident', inst.last_incident],
        ] as const).map(([label, snapshot]) => (
          <details key={label}>
            <summary style={{ fontSize: 12, cursor: 'pointer' }}>{label}</summary>
            {snapshot
              ? <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(snapshot, null, 2)}</pre>
              : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not reported</span>}
          </details>
        ))}
      </div>

      {/* Orgs nested under this install — the install's impacted tenants */}
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700 }}>Orgs on this install</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Tenants affected by changes to this install</span>
        </div>
        {orgs.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No orgs on this install</div>
        ) : (
          <div>
            {orgs.map((o) => (
              <div key={o.external_org_id}>
                <OrgRow o={o} moduleCatalog={moduleCatalog} onToggleModule={onToggleModule} />
                {moduleErrors?.[o.external_org_id] && (
                  <div style={{ fontSize: 11, color: 'var(--status-expired)', marginTop: -4, marginBottom: 4, paddingLeft: 4 }}>
                    {moduleErrors[o.external_org_id]}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Version adoption bars ─────────────────────────────────────────────────
function VersionAdoption({
  versions, installCount, targetVersion,
}: {
  versions: Array<{ version: string; count: number }>;
  installCount: number;
  targetVersion: string | null;
}) {
  const total = versions.reduce((acc, v) => acc + v.count, 0) || installCount || 0;
  // Channel targets are stored with a leading "v" (e.g. "v5.3.0") while
  // version_distribution reports bare semver ("5.3.0"). Normalise both before
  // matching so the "current target" pill lands on the right bar.
  const normVer = (s: string | null) => (s ? s.replace(/^v/i, '') : s);
  const target = normVer(targetVersion);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={panelTitle}>Version adoption</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{installCount} install{installCount !== 1 ? 's' : ''}</span>
      </div>
      {versions.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No installs have checked in yet.</div>
      ) : (
        versions.map((v) => {
          const pct = total > 0 ? Math.round((v.count / total) * 100) : 0;
          const isTarget = target != null && normVer(v.version) === target;
          return (
            <div key={v.version} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 13 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--mono, monospace)', fontWeight: 700 }}>v{v.version}</span>
                  {isTarget && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 99, background: 'var(--ps-pill-green-bg)', color: 'var(--ps-pill-green-tx)' }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--ps-pill-green-tx)' }} /> current target
                    </span>
                  )}
                </div>
                <span style={{ fontWeight: 600 }}>{v.count}/{total} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({pct}%)</span></span>
              </div>
              <div style={{ height: 8, background: 'var(--bg-muted)', borderRadius: 99 }}>
                <div style={{ height: '100%', width: `${pct}%`, background: isTarget ? 'var(--status-active)' : 'var(--text-muted)', borderRadius: 99, transition: 'width .3s' }} />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Fleet telemetry 2×2 (aggregate now-populated data) ────────────────────
function TelemetryCard({ icon: Icon, label, value, color }: { icon: typeof ShieldAlert; label: string; value: React.ReactNode; color: string }) {
  return (
    <div style={{ padding: '12px 14px', background: 'var(--bg-muted)', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon size={13} color="var(--text-muted)" />
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────
export function FleetPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<FleetOverview | null>(null);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [orgs, setOrgs] = useState<OrgAlloc[]>([]);
  const [targetVersion, setTargetVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moduleErrors, setModuleErrors] = useState<Record<string, string>>({});
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogRow[]>([]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [ov, inst, og] = await Promise.all([
        apiRequest<FleetOverview>('/fleet'),
        apiRequest<Instance[]>('/fleet/instances'),
        apiRequest<OrgAlloc[]>('/fleet/orgs'),
      ]);
      setOverview(ov); setInstances(inst || []); setOrgs(og || []);
      // Current rollout target drives the "current target" pill + bar colour.
      // Authoritative source = the same channel targets the Updates page reads.
      try {
        const targets = await apiRequest<ChannelTarget[]>('/distribution/channels');
        const stable = (targets || []).find((t) => t.channel === 'stable') || (targets || [])[0];
        setTargetVersion(stable?.target_version ?? null);
      } catch { setTargetVersion(null); }
    } catch (e: any) {
      setError(e?.message || 'Failed to load fleet');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Module catalog drives the per-org grant switches. Read-only; a failure
    // just leaves the switches absent (rows still render) rather than blocking.
    fetchModulesCatalog()
      .then(({ modules }) => setModuleCatalog(modules))
      .catch(() => setModuleCatalog([]));
  }, []);

  // Dispatch-Op: deep-link to Remote Operations with this install preselected.
  const dispatchTo = (instanceId: string) => navigate(`/remote-ops?instance=${encodeURIComponent(instanceId)}`);

  // Per-module grant toggle: optimistic update, revert + per-org error on
  // failure. PATCHes the org's WHOLE modules object ({...o.modules,[code]:next}).
  const toggleModule = async (o: OrgAlloc, code: string) => {
    if (!o.id) return;
    const next = !(o.modules?.[code] === true);
    const nextModules = { ...(o.modules || {}), [code]: next };
    // Optimistic update
    setOrgs(prev => prev.map(row => row.external_org_id === o.external_org_id ? { ...row, modules: nextModules } : row));
    setModuleErrors(prev => { const e = { ...prev }; delete e[o.external_org_id]; return e; });
    try {
      await apiRequest(`/fleet/orgs/${o.id}/modules`, {
        method: 'PATCH',
        body: JSON.stringify({ modules: nextModules }),
      });
    } catch (err: any) {
      // Revert on failure
      setOrgs(prev => prev.map(row => row.external_org_id === o.external_org_id ? { ...row, modules: o.modules ?? null } : row));
      setModuleErrors(prev => ({ ...prev, [o.external_org_id]: err?.message || 'Failed to update modules' }));
    }
  };

  const t = overview?.totals;
  const kpis = useMemo(() => ([
    { label: 'Installs', value: t?.instances ?? 0, color: undefined as string | undefined },
    { label: 'Healthy (≤7d)', value: t?.healthy ?? 0, color: 'var(--ps-pill-green-tx)' },
    { label: 'Stale', value: t?.stale ?? 0, color: 'var(--ps-pill-amber-tx)' },
    { label: 'Platform licenses', value: t?.platform_licenses ?? 0, color: undefined },
    { label: 'Active orgs', value: t?.active_orgs ?? 0, color: undefined },
  ]), [t]);

  // INSTALL↔ORGS join: bucket every org under the install(s) whose license set
  // contains org.license_id. Any org matching NO install is surfaced separately so
  // none is lost (shouldn't happen given the two-tier model, but is a safe backstop).
  const { orgsByInstance, unassignedOrgs } = useMemo(() => {
    const byInstance = new Map<string, OrgAlloc[]>();
    instances.forEach((i) => byInstance.set(i.instance_id, []));
    const matched = new Set<string>();
    for (const o of orgs) {
      const lic = o.license_id;
      if (!lic) continue;
      for (const inst of instances) {
        if (instanceLicenseSet(inst).has(lic)) {
          byInstance.get(inst.instance_id)!.push(o);
          matched.add(o.external_org_id);
        }
      }
    }
    const unassigned = orgs.filter((o) => !matched.has(o.external_org_id));
    return { orgsByInstance: byInstance, unassignedOrgs: unassigned };
  }, [instances, orgs]);

  // Telemetry summary values (fleet-wide).
  const vt = overview?.vuln_totals;
  const totalUsers = instances.reduce((acc, i) => acc + (i.user_count ?? 0), 0);
  const dbReports = instances.filter((i) => i.db_size_mb != null);
  const avgDb = dbReports.length ? Math.round(dbReports.reduce((acc, i) => acc + (i.db_size_mb ?? 0), 0) / dbReports.length) : null;
  const critVulns = vt?.critical ?? 0;
  const highVulns = vt?.high ?? 0;
  const backedUp = t?.backed_up ?? 0;
  const lastBackupAt = overview?.last_backup_at ?? null;

  if (loading) return <div style={{ padding: 24 }}>Loading fleet…</div>;
  if (error) return <div style={{ padding: 24, color: 'var(--status-expired)' }}>Error: {error}</div>;

  return (
    <div style={{ padding: '24px 32px' }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Server size={22} />
          <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.01em', margin: 0 }}>Fleet</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/remote-ops')} style={btnSec}><Terminal size={13} />Dispatch to Fleet</button>
          <button onClick={load} style={btnSec}><RefreshCw size={13} />Refresh</button>
        </div>
      </div>

      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 22 }}>
        {kpis.map((k) => (
          <Card key={k.label} style={{ borderColor: 'var(--border)', boxShadow: 'var(--shadow-card)', padding: '16px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 8 }}>{k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: k.color || 'var(--text-primary)', letterSpacing: '-.01em', lineHeight: 1 }}>{k.value}</div>
          </Card>
        ))}
      </div>

      {/* Version adoption + telemetry row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card style={{ borderColor: 'var(--border)', boxShadow: 'var(--shadow-card)', padding: '18px 20px' }}>
          <VersionAdoption
            versions={overview?.version_distribution || []}
            installCount={t?.instances ?? instances.length}
            targetVersion={targetVersion}
          />
        </Card>
        <Card style={{ borderColor: 'var(--border)', boxShadow: 'var(--shadow-card)', padding: '18px 20px' }}>
          <div style={{ ...panelTitle, marginBottom: 14 }}>Fleet telemetry</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <TelemetryCard icon={ShieldAlert} label="Critical vulns" value={critVulns} color={critVulns > 0 ? 'var(--status-expired)' : 'var(--text-muted)'} />
            <TelemetryCard icon={AlertTriangle} label="High vulns" value={highVulns} color={highVulns > 0 ? 'var(--ps-pill-amber-tx)' : 'var(--text-muted)'} />
            <TelemetryCard icon={Database} label="Avg DB size" value={avgDb != null ? `${avgDb.toLocaleString()} MB` : '—'} color="var(--text-primary)" />
            <TelemetryCard icon={Users} label="Total users" value={totalUsers} color="var(--text-primary)" />
            <TelemetryCard
              icon={HardDriveDownload}
              label="Backed up"
              value={lastBackupAt ? `${backedUp}/${t?.instances ?? 0} · ${formatRelativeTime(lastBackupAt)}` : `${backedUp}/${t?.instances ?? 0}`}
              color={backedUp > 0 ? 'var(--text-primary)' : 'var(--text-muted)'}
            />
          </div>
        </Card>
      </div>

      {/* Installs as per-instance cards, each with its impacted tenants nested */}
      <div style={sectionCard}>
        <div style={cardHeaderRow}>
          <span style={cardTitle}>Installs</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{instances.length} total</span>
        </div>
        {instances.length === 0 ? (
          <EmptyState
            icon="server"
            title="No installs yet"
            // D-27 — Fleet is structurally install/platform-only (phone-home
            // telemetry a saas-class license never sends). Trust SaaS orgs live
            // under Organizations (ledger=SaaS) and Trust org metrics instead.
            body="No installs have checked in. Once an install phones home it will appear here with live health and telemetry. Trust SaaS orgs don't phone home — find them under Organizations (Ledger = SaaS) or Trust org metrics."
          />
        ) : (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {instances.map((i) => (
              <InstanceCard
                key={i.instance_id}
                inst={i}
                orgs={orgsByInstance.get(i.instance_id) || []}
                onDispatch={dispatchTo}
                moduleCatalog={moduleCatalog}
                onToggleModule={toggleModule}
                moduleErrors={moduleErrors}
              />
            ))}
          </div>
        )}
      </div>

      {/* Backstop: any org that matched no install (shouldn't happen) */}
      {unassignedOrgs.length > 0 && (
        <div style={{ ...sectionCard, marginBottom: 0 }}>
          <div style={cardHeaderRow}><span style={cardTitle}>Unassigned orgs</span></div>
          <div style={{ padding: '12px 20px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              These active org allocations did not match any reporting install (no license overlap). Surfaced here so none is lost.
            </div>
            {unassignedOrgs.map((o) => <OrgRow key={o.external_org_id} o={o} moduleCatalog={moduleCatalog} onToggleModule={toggleModule} />)}
          </div>
        </div>
      )}
    </div>
  );
}
